import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { isMobile } from 'lib/device'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { usePanelVirtualizer } from 'lib/usePanelVirtualizer'
import PanelLoadingIndicator from './PanelLoadingIndicator'
import { RadioSongItem } from './MusicArtistProfile'
import { DISCOVER_SONG_GAP_PX, DiscoverSong, discoverSongRowHeight } from './radioArtistsModel'
import { ARTISTS_GC_MS, ARTISTS_STALE_MS } from './radioArtistsParts'
import styles from './radio-artists.module.scss'

export type DiscoverPage = { songs: DiscoverSong[]; has_more: boolean; window?: string | null }

// Rows kept mounted past the viewport, and how close to the end of the loaded
// songs the next page starts loading (about the regular feed's 3200px).
const DISCOVER_OVERSCAN = 6
const DISCOVER_PREFETCH_ROWS = 8

// A Music song list (Discover, My Music). It loads and windows like the regular
// thread feed: pages load ahead of the scroll, and only rows near the viewport
// mount. A song's height follows from the panel width alone (the card is 16:9),
// so each row's height is exact before it renders and nothing shifts while
// scrolling.
export default function MusicSongFeed({
    queryKey,
    fetchPage,
    source,
    emptyText,
    testId,
}: {
    queryKey: unknown[]
    fetchPage: (page: number) => Promise<DiscoverPage>
    source: 'music_discover' | 'my_music'
    emptyText: string
    testId: string
}) {
    const { scrollContainerRef } = usePanelContext()

    // Each order is its own list.
    const query = useInfiniteQuery<DiscoverPage>({
        queryKey,
        staleTime: ARTISTS_STALE_MS,
        gcTime: ARTISTS_GC_MS,
        refetchOnMount: true,
        placeholderData: keepPreviousData,
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => (lastPage?.has_more ? pages.length + 1 : undefined),
        queryFn: ({ pageParam }) => fetchPage(pageParam as number),
    })

    // Switching order keeps the old list as placeholder data. It stays in place,
    // faded, with the loader over its top: emptying it shrank the page and the
    // panel jumped to the top (phones, first tap on an order).
    const switching = query.isPlaceholderData && query.isFetching

    // Ranks shift between page fetches, so offset paging can repeat a song on a
    // later page; keep the first.
    const songs = useMemo(() => {
        const seen = new Set<string>()
        return (query.data?.pages || [])
            .flatMap(page => page?.songs || [])
            .filter(song => !seen.has(song.answer_id) && !!seen.add(song.answer_id))
    }, [query.data?.pages])

    const rootRef = useRef<HTMLDivElement>(null)
    const [listWidth, setListWidth] = useState(0)
    useLayoutEffect(() => {
        const element = rootRef.current
        if (!element) return undefined
        const measure = () => setListWidth(element.getBoundingClientRect().width)
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    // Row keys carry the list they belong to. The virtualizer holds the scroll on
    // a row by its key, so a song shared by two orders would drag the screen to
    // its new position; a new order is a new list, not a reorder. The old list's
    // placeholder keeps its keys until the new data arrives.
    const listKey = JSON.stringify(queryKey)
    const shownListKeyRef = useRef(listKey)
    if (!query.isPlaceholderData) shownListKeyRef.current = listKey
    const shownListKey = shownListKeyRef.current

    const rowHeight = discoverSongRowHeight(listWidth, isMobile)
    const staticRows = useMemo(
        () =>
            songs.map(song => ({
                key: `${shownListKey}:${song.answer_id}`,
                estimatedHeight: rowHeight,
                staticSignature: String(rowHeight),
                resizeIntentSignature: null,
            })),
        [rowHeight, shownListKey, songs],
    )
    const { listElementRef, measureElement, totalSize, virtualItems, virtualListStart } =
        usePanelVirtualizer({
            count: songs.length,
            enabled: listWidth > 0,
            resolveScrollElement: () => scrollContainerRef?.current || null,
            getItemKey: index => staticRows[index]?.key ?? `discover-song-${index}`,
            estimateSize: index => staticRows[index]?.estimatedHeight ?? rowHeight,
            overscan: DISCOVER_OVERSCAN,
            gap: DISCOVER_SONG_GAP_PX,
            staticGeometry: true,
            staticRows,
            allowMeasuredShrinkAdjustment: true,
            retainMeasuredScrollMovement: false,
            retainBackwardMeasuredScrollMovement: true,
            retainBackwardVirtualStartOnTotalSizeChange: true,
            retainVirtualStartMovement: true,
        })

    const { hasNextPage, isFetching, isFetchingNextPage, isFetchNextPageError, fetchNextPage } =
        query
    const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? -1
    // No rows yet counts as near the end: a page can serialize to nothing (its
    // songs lost their media) and the next one still has to load.
    const nearEnd = lastVisibleIndex >= songs.length - DISCOVER_PREFETCH_ROWS
    // Waits out any fetch in flight: fetching a page cancels a refresh of the
    // loaded ones. A failed page waits for Retry instead of refetching while the
    // end stays near.
    useEffect(() => {
        if (nearEnd && hasNextPage && !isFetching && !isFetchNextPageError) {
            void fetchNextPage()
        }
    }, [fetchNextPage, hasNextPage, isFetchNextPageError, isFetching, nearEnd])

    return (
        <div
            className={cn(styles.profile, switching && styles.profileSwitching)}
            ref={rootRef}
            data-testid={testId}
            aria-busy={switching || undefined}
        >
            {query.isLoading && <PanelLoadingIndicator label="Loading songs" />}
            {switching && (
                <div className={styles.switchingLoader}>
                    <PanelLoadingIndicator label="Loading songs" />
                </div>
            )}
            {query.isError && <div className={styles.empty}>Couldn't load songs.</div>}
            {query.isSuccess && !switching && songs.length === 0 && (
                <div className={styles.empty}>{emptyText}</div>
            )}
            <div
                ref={listElementRef}
                className={styles.discoverList}
                style={{ height: totalSize }}
                data-testid={`${testId}-list`}
            >
                {virtualItems.map(virtualItem => {
                    const song = songs[virtualItem.index]
                    if (!song) return null
                    return (
                        <div
                            key={virtualItem.key}
                            ref={measureElement}
                            data-index={virtualItem.index}
                            className={styles.discoverRow}
                            style={{
                                height: virtualItem.size,
                                transform: `translateY(${virtualItem.start - virtualListStart}px)`,
                            }}
                        >
                            <RadioSongItem
                                answerId={song.answer_id}
                                title={song.title}
                                source={source}
                            />
                        </div>
                    )
                })}
            </div>
            {isFetchingNextPage && <PanelLoadingIndicator label="Loading more songs" />}
            {isFetchNextPageError && (
                <button
                    type="button"
                    className={styles.retryButton}
                    onClick={() => void fetchNextPage()}
                >
                    Couldn't load more songs. Retry
                </button>
            )}
        </div>
    )
}
